import React, { useMemo, useState } from "react";
import { Select, Spin } from "antd";
import NoDataComponent from "./NoData";
import i18n from "i18next";

const { Option } = Select;

interface OptionItem<T> {
  value: T;
  label: string;
}

interface CommonMultiSelectWithSearchProps<T extends string | number> {
  value?: T[];
  onChange?: (value: T[]) => void;
  placeholder?: string;
  loading?: boolean;
  options: OptionItem<T>[];
  disabled?: boolean;
  /** Minimum characters before showing results */
  minSearchChars?: number; // default 3
  /** Limit how many filtered options to render (perf) */
  limit?: number; // default 200
}

const normalize = (s: string) =>
  (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const CommonMultiSelectWithSearch = <T extends string | number>({
  value,
  onChange,
  placeholder = i18n.t("select_item"),
  loading = false,
  options,
  disabled = false,
  minSearchChars = 3,
  limit = 200,
}: CommonMultiSelectWithSearchProps<T>) => {
  const [query, setQuery] = useState("");

  // Map value -> label for quick lookups
  const valueToLabel = useMemo(() => {
    const m = new Map<T, string>();
    for (const o of options) m.set(o.value, o.label);
    return m;
  }, [options]);

  // Selected options (ensure they are always present in the dropdown)
  const selectedOptions: OptionItem<T>[] = useMemo(() => {
    if (!value?.length) return [];
    const seen = new Set<T>();
    const list: OptionItem<T>[] = [];
    for (const v of value) {
      if (seen.has(v)) continue;
      seen.add(v);
      list.push({ value: v, label: valueToLabel.get(v) ?? String(v) });
    }
    return list;
  }, [value, valueToLabel]);

  // Filtered options (when query is long enough)
  const filtered = useMemo(() => {
    if (query.length < minSearchChars) return [] as OptionItem<T>[];
    const q = normalize(query);
    const result = options.filter(
      (opt) => normalize(opt.label).includes(q) || normalize(String(opt.value)).includes(q)
    );
    return result.slice(0, limit);
  }, [options, query, minSearchChars, limit]);

  // Union: selected options + filtered results (dedup by value)
  const displayOptions = useMemo(() => {
    const map = new Map<T, string>();
    for (const o of selectedOptions) map.set(o.value, o.label);
    for (const o of filtered) if (!map.has(o.value)) map.set(o.value, o.label);
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [selectedOptions, filtered]);

  const notFound =
    loading ? (
      <Spin size="small" />
    ) : query.length < minSearchChars ? (
      <div style={{ padding: 8, color: "#6b7280", fontSize: 13 }}>
        {i18n.t("type_min_chars_to_search", {
          count: minSearchChars,
          defaultValue: `Type at least ${minSearchChars} characters to search`,
        })}
      </div>
    ) : (
      <NoDataComponent />
    );

  return (
    <Select
      disabled={disabled}
      mode="multiple"
      showSearch
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onSearch={setQuery}          // client-side search
      filterOption={false}         // we handle filtering
      notFoundContent={notFound}
      style={{ width: "100%" }}
    //   maxTagCount="responsive"
      virtual
      onDropdownVisibleChange={(open) => {
        if (!open) setQuery("");   // reset when closing
      }}
      tagRender={({ label }) => (
        <span className="badge bg-outline-primary">{label}</span>
      )}
    >
      {displayOptions.map((o) => (
        <Option key={String(o.value)} value={o.value}>
          {o.label}
        </Option>
      ))}
    </Select>
  );
};

export default CommonMultiSelectWithSearch;
